import { useEffect, useState, useCallback, useMemo, ReactNode } from 'react'
import { supabase } from '../lib/supabase'
import type { Database } from '../types/supabase'
import { DEFAULT_SUBSCRIPTION, SubscriptionContext } from './subscription-context'
import type { SubscriptionContextValue, SubscriptionInfo, SubscriptionStatus, SubscriptionTier } from './subscription-context'

export type { SubscriptionTier, SubscriptionStatus } from './subscription-context'

type SubscriptionRow = Database['public']['Tables']['subscriptions']['Row']
type SubscriptionPlanRow = Database['public']['Tables']['subscription_plans']['Row']

interface SubscriptionWithPlan {
  subscriptions: SubscriptionRow & {
    subscription_plans: SubscriptionPlanRow
  }
}

function resolveTier(
  planTier: string | null | undefined,
  status: string | null,
  currentPeriodEnd: string | null
): SubscriptionTier {
  const planTierTyped = (planTier as SubscriptionTier) || 'free'
  const statusTyped = status as SubscriptionStatus | null

  if (planTierTyped === 'free') return 'free'

  if (statusTyped === 'active') return planTierTyped

  if (statusTyped === 'canceled') {
    if (currentPeriodEnd && new Date(currentPeriodEnd) > new Date()) {
      return planTierTyped
    }
    return 'free'
  }

  return 'free'
}

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo>(DEFAULT_SUBSCRIPTION)
  const [userId, setUserId] = useState<string | null>(null)

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      const newUserId = s?.user?.id ?? null
      setUserId(prev => prev === newUserId ? prev : newUserId)
    })
    return () => subscription.unsubscribe()
  }, [])

  const fetchSubscription = useCallback(async () => {
    if (!userId) {
      setSubscriptionInfo({ ...DEFAULT_SUBSCRIPTION })
      return
    }

    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*, subscription_plans(*)')
        .eq('user_id', userId)
        .maybeSingle()

      if (error) {
        console.error('[SubscriptionContext] Error fetching subscription:', error)
        setSubscriptionInfo({ ...DEFAULT_SUBSCRIPTION, loaded: true })
        return
      }

      if (!data) {
        setSubscriptionInfo({ ...DEFAULT_SUBSCRIPTION, loaded: true })
        return
      }

      const sub = data as unknown as SubscriptionWithPlan['subscriptions']
      const plan = sub.subscription_plans
      const status = (sub.status as SubscriptionStatus) || 'inactive'
      const periodEnd = sub.current_period_end

      setSubscriptionInfo({
        tier: resolveTier(plan?.tier, status, periodEnd),
        status,
        maxSubmissionsPerMonth: plan?.max_submissions_per_month ?? null,
        currentPeriodEnd: periodEnd,
        cancelAtPeriodEnd: sub.cancel_at_period_end ?? false,
        polarSubscriptionId: sub.polar_subscription_id,
        loaded: true,
      })
    } catch (err) {
      console.error('[SubscriptionContext] Unexpected error:', err)
      setSubscriptionInfo({ ...DEFAULT_SUBSCRIPTION, loaded: true })
    }
  }, [userId])

  useEffect(() => {
    fetchSubscription()
  }, [fetchSubscription])

  useEffect(() => {
    if (!userId) return

    const channel = supabase
      .channel('subscription-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          fetchSubscription()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [userId, fetchSubscription])

  const contextValue = useMemo<SubscriptionContextValue>(
    () => ({ ...subscriptionInfo, refresh: fetchSubscription }),
    [subscriptionInfo, fetchSubscription]
  )

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  )
}